import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type { BenefitPlanGroup } from "@platform/btx-plan-designer";
import type { AxiosInstance } from "axios";

/**
 * Partial body for PATCH /btx/v1/benefit-plan-groups/:id. Only the free-text
 * fields this hook owns; the endpoint leaves omitted fields unchanged.
 */
type UpdateBenefitPlanGroupFootnoteRequest = Partial<
  Pick<BenefitPlanGroup, "footnote" | "footnoteHeaderLabel" | "illustratedFrom">
>;

const patchBenefitPlanGroupFootnote = async (
  axiosInstance: AxiosInstance,
  groupId: string,
  fields: UpdateBenefitPlanGroupFootnoteRequest,
): Promise<void> => {
  await axiosInstance.patch(`btx/v1/benefit-plan-groups/${groupId}`, fields);
};

interface UseBenefitPlanGroupFootnoteMutationOptions {
  ecoRfpId: string;
  axiosInstance: AxiosInstance;
  onSaveError?: (err: Error) => void;
}

export const useBenefitPlanGroupFootnoteMutation = ({
  ecoRfpId,
  axiosInstance,
  onSaveError,
}: UseBenefitPlanGroupFootnoteMutationOptions): {
  saveFootnote: (groupId: string, footnote: string) => void;
  saveFootnoteHeaderLabel: (groupId: string, label: string) => void;
  saveIllustratedFrom: (groupId: string, illustratedFrom: string) => void;
} => {
  const queryClient = useQueryClient();
  const queryKey = ["quotes-benefit-plan-groups", ecoRfpId];

  const { mutate } = useMutation({
    mutationFn: ({
      groupId,
      fields,
    }: {
      groupId: string;
      fields: UpdateBenefitPlanGroupFootnoteRequest;
    }) => patchBenefitPlanGroupFootnote(axiosInstance, groupId, fields),
    onMutate: async ({
      groupId,
      fields,
    }: {
      groupId: string;
      fields: UpdateBenefitPlanGroupFootnoteRequest;
    }) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueriesData<BenefitPlanGroup[]>({
        queryKey,
      });

      // Matched by group id, never by list position — a reorder must not change
      // which group a saved label belongs to.
      queryClient.setQueriesData<BenefitPlanGroup[]>({ queryKey }, (old) =>
        old?.map((group) =>
          group.id === groupId ? { ...group, ...fields } : group,
        ),
      );

      return { previous };
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey, refetchType: "none" }),
    onError: (err: Error, _vars, context) => {
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      onSaveError?.(err);
    },
  });

  const saveFootnote = useCallback(
    (groupId: string, footnote: string) =>
      mutate({ groupId, fields: { footnote } }),
    [mutate],
  );

  const saveFootnoteHeaderLabel = useCallback(
    (groupId: string, label: string) =>
      mutate({ groupId, fields: { footnoteHeaderLabel: label } }),
    [mutate],
  );

  const saveIllustratedFrom = useCallback(
    (groupId: string, illustratedFrom: string) =>
      mutate({ groupId, fields: { illustratedFrom } }),
    [mutate],
  );

  return { saveFootnote, saveFootnoteHeaderLabel, saveIllustratedFrom };
};
